/**
 * Client-side image resize helper — Sprint 10.5G Group 3 (#5).
 *
 * The Workers runtime has no native image decoder and our plan does not
 * include Cloudflare Images, so we proportionally downscale logos and
 * favicons in the browser using `<canvas>` before upload. This keeps the
 * backend simple (just validates post-resize size + MIME) and is fast
 * enough for the small image sizes we target.
 *
 * SVGs are passed through unchanged — they are already resolution-
 * independent and re-rasterising them would defeat the point. `.ico`
 * files are also passed through because canvas cannot encode them.
 *
 * See ADR-020 for the decision rationale.
 */

export interface ResizeOptions {
  /** Max output width in CSS pixels. Aspect ratio is preserved. */
  maxWidth: number;
  /** Max output height in CSS pixels. Aspect ratio is preserved. */
  maxHeight: number;
  /**
   * Output MIME type. Defaults to `image/png` which preserves transparency
   * (important for logos + favicons). Pass `image/jpeg` for photographs.
   */
  outputType?: 'image/png' | 'image/jpeg' | 'image/webp';
  /** 0–1 quality for lossy formats. Defaults to 0.92. */
  quality?: number;
}

const PASSTHROUGH_TYPES = new Set([
  'image/svg+xml',
  'image/x-icon',
  'image/vnd.microsoft.icon',
]);

/**
 * Resize an image file to fit inside `maxWidth × maxHeight` while preserving
 * aspect ratio. Returns a new File with the same base name + resized extension.
 *
 * If the input is already within bounds, the canvas still re-encodes it —
 * this normalises the MIME type and strips metadata (EXIF, colour profiles)
 * which is usually desirable for user-uploaded branding assets.
 */
export async function resizeImage(file: File, opts: ResizeOptions): Promise<File> {
  if (PASSTHROUGH_TYPES.has(file.type)) {
    return file;
  }

  const { maxWidth, maxHeight, outputType = 'image/png', quality = 0.92 } = opts;

  const bitmap = await loadBitmap(file);
  try {
    const { width, height } = fitInside(bitmap.width, bitmap.height, maxWidth, maxHeight);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context unavailable');

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, width, height);

    const blob = await canvasToBlob(canvas, outputType, quality);
    const ext = outputType === 'image/jpeg' ? 'jpg' : outputType.split('/')[1];
    const baseName = file.name.replace(/\.[^.]+$/, '') || 'image';
    return new File([blob], `${baseName}.${ext}`, { type: outputType });
  } finally {
    if ('close' in bitmap && typeof bitmap.close === 'function') {
      bitmap.close();
    }
  }
}

async function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(file);
  }
  // Safari/older fallback — load via HTMLImageElement + object URL.
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to decode image'));
    };
    img.src = url;
  });
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Canvas encoding failed'));
      },
      type,
      quality,
    );
  });
}

/**
 * Compute target dimensions that fit inside a bounding box while preserving
 * aspect ratio. If the source already fits, returns it unchanged.
 *
 * Exported for unit testing — the canvas path itself is hard to exercise in
 * JSDOM, but this maths is the part that matters most.
 */
export function fitInside(
  srcW: number,
  srcH: number,
  maxW: number,
  maxH: number,
): { width: number; height: number } {
  if (srcW <= 0 || srcH <= 0) {
    return { width: maxW, height: maxH };
  }
  const scale = Math.min(maxW / srcW, maxH / srcH, 1);
  return {
    width: Math.max(1, Math.round(srcW * scale)),
    height: Math.max(1, Math.round(srcH * scale)),
  };
}

// Recommended bounding boxes — also enforced on the server as a sanity check.
export const LOGO_BOUNDS: ResizeOptions = {
  maxWidth: 400,
  maxHeight: 100,
  outputType: 'image/png',
};

export const FAVICON_BOUNDS: ResizeOptions = {
  maxWidth: 128,
  maxHeight: 128,
  outputType: 'image/png',
};
